import { CSSProperties, ReactNode } from 'react';
import ActingAsUserMessage from './acting-as-user-message';
import SharedSvg, { SharedSvgVariant } from './shared-svg';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import SharedDialogOld from './shared-dialog-old';
import SharedButton from './buttons/shared-button';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';

interface SharedDialogButton {
  action: () => void;
  title: string;
  titleIconVariant?: SharedSvgVariant;
  color?: 'primary' | 'warning' | 'error';
  loading?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
  sx?: SxProps<Theme>;
}

interface SharedDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  titleIconVariant?: SharedSvgVariant;
  /**
   * DO NOT add padding or margin on the root element; DialogContent in SharedDialog owns spacing.
   * When including form fields, set autoFocus on the first input.
   */
  dialogContent?: ReactNode;
  /** When set, shows an "Acting as" message above dialogContent. */
  actingAs?: string;
  primaryButton: SharedDialogButton;
  secondaryButton?: SharedDialogButton;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  noCancel?: boolean;
  buttonAlignment?: CSSProperties['justifyContent'];
}

export default function SharedDialog({
  open,
  handleClose,
  title,
  dialogContent,
  actingAs,
  primaryButton,
  secondaryButton,
  maxWidth,
  noCancel = false,
  buttonAlignment,
  titleIconVariant = 'red',
}: SharedDialogProps) {
  const { features } = useUserSettings();
  const theme = useTheme();

  if (!isFeatureEnabled('uiEnhancementPhase2', features)) {
    return (
      <SharedDialogOld
        open={open}
        handleClose={handleClose}
        title={title}
        dialogContent={dialogContent}
        actingAs={actingAs}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        maxWidth={maxWidth}
        noCancel={noCancel}
        buttonAlignment={buttonAlignment}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth ?? 'sm'}
      // https://github.com/mui/material-ui/issues/33004#issuecomment-1455260156
      // autoFocus on the first input will not work if disableRestoreFocus isn't true
      disableRestoreFocus
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: theme.palette.semantic.surface.bg1,
          border:
            theme.palette.mode === 'dark'
              ? `0.5px solid ${theme.palette.semantic.outline.light}`
              : 'none',
          borderRadius: '8px',
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'unset'
              : '0 1px 20px 4px rgba(0, 0, 0, 0.12)',
        },
      }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: '12px',
          pl: ' 16px',
          pr: '8px',
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <SharedSvg
            sx={{ mr: 2 }}
            variant={titleIconVariant}
            width={16}
            height={16}
          />
          <Typography
            variant="h6"
            component="span"
            sx={{ color: theme.palette.semantic.text.title, pt: 0.2 }}
          >
            {title}
          </Typography>
        </Stack>{' '}
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ padding: 4, '& strong': { fontWeight: 700 } }}>
        {actingAs && <ActingAsUserMessage userEmail={actingAs} />}
        {dialogContent}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            width: '100%',
            justifyContent: buttonAlignment ?? 'flex-end',
          }}
        >
          {!noCancel && (
            <SharedButton variant="secondary" onClick={handleClose}>
              Cancel
            </SharedButton>
          )}

          {secondaryButton && (
            <SharedButton
              loading={secondaryButton.loading ?? false}
              disabled={secondaryButton.disabled ?? false}
              variant="secondary"
              onClick={secondaryButton.action}
              sx={secondaryButton.sx}
              color={secondaryButton.color}
            >
              {secondaryButton.title}
            </SharedButton>
          )}
          <SharedButton
            loading={primaryButton.loading ?? false}
            variant="primary"
            onClick={primaryButton.action}
            disabled={primaryButton.disabled ?? false}
            color={primaryButton.color}
            startIcon={primaryButton.startIcon}
            sx={{ height: '100%', ...primaryButton.sx }}
          >
            {primaryButton.title}
          </SharedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
